import { usesHostedBilling } from '@/lib/appDistribution';
import React, { useState, useCallback } from 'react';
import { StyleSheet, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useAuthStore, useToastStore } from '@/store';
import { trackEvent } from '@/constants/utils/analytics';
import { creditPacksApi } from '@/lib/api';
import { openBtcPayCheckout, btcPayCheckoutOpenError } from '@/lib/btcpayCheckout';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 5,
    marginTop: 2,
    cursor: 'pointer',
    opacity: 0.85,
  },
  rowHovered: {
    opacity: 1,
  },
});

// Web and direct-distributed macOS use BTCPay. Mobile stays on store billing.
// Completing payment is handled by /webhooks/btcpay.
function PayWithBitcoinRow({ creditPackId, price, disabled, style }) {
  const { colors } = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  const handlePress = useCallback(async () => {
    if (!usesHostedBilling()) return;
    if (!creditPackId) return;
    const { addToast } = useToastStore.getState().actions;
    const expectedUserId = Platform.OS === 'macos' ? (useAuthStore.getState().user?.id ?? null) : undefined;
    setIsLoading(true);
    trackEvent('conversion', 'credit_pack_selected', 'bitcoin', price);
    try {
      const response = await creditPacksApi.createBtcPayInvoice({ creditPackId });
      const checkoutUrl = response?.checkoutUrl || response?.data?.checkoutUrl;
      if (checkoutUrl) {
        await openBtcPayCheckout({
          checkoutUrl,
          invoiceId: response?.invoiceId || response?.data?.invoiceId,
          expectedUserId,
          kind: 'credits',
          onSettled: () => {
            if (Platform.OS !== 'macos') useAuthStore.getState().fetchUserCredits?.({ force: true });
            addToast('Bitcoin payment received. Credits have been added.', 'success');
          },
        });
        addToast('Bitcoin payment opened. Complete payment to receive credits.', 'info');
      } else {
        const openError = btcPayCheckoutOpenError(response);
        addToast(openError.message, openError.type);
      }
    } catch (error) {
      console.error('Bitcoin credit pack checkout error:', error);
      addToast(error?.message || 'Failed to create Bitcoin payment', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [creditPackId, price]);

  if (!usesHostedBilling()) return null;

  return (
    <Pressable
      style={({ hovered }) => [
        styles.row,
        hovered && styles.rowHovered,
        style,
      ]}
      onPress={handlePress}
      disabled={disabled || isLoading || !creditPackId}
      accessibilityRole="button"
      accessibilityLabel="Pay with Bitcoin"
    >
      <Text size="s" color="secondary">or </Text>
      <Icon name="bitcoin-sign" size={11} color={colors.text_color_secondary} />
      <Text size="s" color="secondary" weight="medium">
        {isLoading ? ' creating invoice...' : ' pay with Bitcoin'}
      </Text>
    </Pressable>
  );
}

export default React.memo(PayWithBitcoinRow);
